import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SignOut,
  House,
  CircleNotch,
  SquaresFour,
  Tray,
  Package,
  Trophy,
  Images,
  BookOpen,
} from "@phosphor-icons/react";
import AdminGuard from "@/components/admin/AdminGuard";
import DashboardPanel from "@/components/admin/DashboardPanel";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import ProductsPanel from "@/components/admin/ProductsPanel";
import AwardsPanel from "@/components/admin/AwardsPanel";
import BrochuresPanel from "@/components/admin/BrochuresPanel";
import MediaPanel from "@/components/admin/MediaPanel";
import { logout } from "@/lib/api";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: SquaresFour, Panel: DashboardPanel },
  { key: "richieste", label: "Richieste", icon: Tray,        Panel: SubmissionsPanel },
  { key: "prodotti",  label: "Prodotti",  icon: Package,     Panel: ProductsPanel },
  { key: "premi",     label: "Premi",     icon: Trophy,      Panel: AwardsPanel },
  { key: "brochure",  label: "Brochure",  icon: BookOpen,    Panel: BrochuresPanel },
  { key: "media",     label: "Media",     icon: Images,      Panel: MediaPanel },
];

export default function AdminPage() {
  return <AdminGuard>{(user) => <Dashboard user={user} />}</AdminGuard>;
}

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [tab, setTab] = useState("dashboard");

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } catch {
      // ignora — sessione probabilmente già scaduta
    }
    navigate("/admin/login", { replace: true });
  };

  const ActivePanel = TABS.find((t) => t.key === tab)?.Panel || DashboardPanel;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAF9F6] lg:flex">
      {/* Sidebar (barra in alto su mobile) */}
      <aside className="bg-[#1A1918] text-[#FAF9F6] lg:w-60 lg:flex-shrink-0 lg:h-screen lg:sticky lg:top-0 flex flex-col sticky top-0 z-40">
        <div className="px-5 lg:px-6 pt-5 lg:pt-7 pb-4 flex items-center justify-between lg:block">
          <div>
            <div className="font-display text-lg font-bold tracking-tight">
              Koreko Direct
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#EB0D0D] mt-1">
              Area riservata
            </div>
          </div>
          <div className="flex items-center gap-1 lg:hidden">
            <button
              onClick={() => navigate("/")}
              className="p-2 text-[#FAF9F6]/70 hover:text-[#FAF9F6]"
              aria-label="Vedi sito"
            >
              <House size={18} weight="bold" />
            </button>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="p-2 text-[#FAF9F6]/70 hover:text-[#EB0D0D] disabled:opacity-50"
              aria-label="Esci"
            >
              {loggingOut ? (
                <CircleNotch size={18} weight="bold" className="animate-spin" />
              ) : (
                <SignOut size={18} weight="bold" />
              )}
            </button>
          </div>
        </div>

        <nav
          className="flex lg:flex-col gap-1 px-3 pb-3 lg:pb-0 overflow-x-auto lg:overflow-visible lg:flex-1"
          role="tablist"
          aria-label="Sezioni admin"
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap lg:w-full ${
                  active
                    ? "bg-[#FAF9F6] text-[#1A1918]"
                    : "text-[#FAF9F6]/70 hover:text-[#FAF9F6] hover:bg-[#FAF9F6]/10"
                }`}
              >
                <Icon size={16} weight="bold" />
                {t.label}
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:block px-3 py-4 border-t border-[#FAF9F6]/10 space-y-1">
          {user?.email && (
            <div className="px-3 pb-2 text-xs text-[#FAF9F6]/50 truncate" title={user.email}>
              {user.email}
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="w-full inline-flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-[#FAF9F6]/70 hover:text-[#FAF9F6] hover:bg-[#FAF9F6]/10 transition-colors"
            data-testid="admin-view-site"
          >
            <House size={16} weight="bold" />
            Vedi sito
          </button>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="w-full inline-flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-[#FAF9F6]/70 hover:text-[#FAF9F6] hover:bg-[#EB0D0D] transition-colors disabled:opacity-50"
            data-testid="admin-logout"
          >
            {loggingOut ? (
              <CircleNotch size={16} weight="bold" className="animate-spin" />
            ) : (
              <SignOut size={16} weight="bold" />
            )}
            Esci
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-5 sm:px-8 lg:px-10 py-8 md:py-12">
        <div className="max-w-[1240px] mx-auto">
          <ActivePanel onNavigate={setTab} />
        </div>
      </main>
    </div>
  );
}
